import React, { useState, useRef, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Alert,
  AlertIcon,
  //Spinner,
  Image,
  Flex,
  Box,
  VStack,
  Text,
  Switch,
  useToast
} from '@chakra-ui/react';

const PromotionBuilderModal = ({ show, onHide, promotion = null, onPromotionSaved }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(true);

  const fileInputRef = useRef(null);
  const toast = useToast();

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const validateForm = () => {
    if (!title.trim()) {
      setError('Promotion title is required');
      return false;
    }
    if (!description.trim()) {
      setError('Promotion description is required');
      return false;
    }
    if (!image) {
      setError('Promotion image is required');
      return false;
    }
    setError('');
    return true;
  };

  // const uploadImage = async () => {
  //   if (!image) return null;
  //   // Upload logic goes here
  //   return 'https://example.com/image.jpg'; // mock image URL
  // };

  useEffect(() => {
    if (promotion) {
      setTitle(promotion.title || '');
      setDescription(promotion.description || '');
      setPreviewUrl(promotion.imageUrl || '');
      setImage(null); // optional: keep it null unless user uploads new one
    } else {
      resetForm();
    }
  }, [promotion, show]);
  

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
  
    setIsLoading(true);
    setError('');
    setSuccess('');
  
    try {
      // let imageUrl = promotion?.imageUrl || '';
      // if (image) {
      //   imageUrl = await uploadImage();
      // }
  
      if (promotion) {
        // Update existing promotion
        // await axios.put(`/api/promotions/${promotion._id}`, {
        //   title,
        //   description,
        //   imageUrl
        // });
  
        toast({ status: 'success', title: 'Updated', description: 'Promotion updated!' });
        setSuccess('Promotion updated successfully!');
      } else {
        // Create new promotion
        // await axios.post('/api/promotions', {
        //   title,
        //   description,
        //   imageUrl
        // });
  
        toast({ status: 'success', title: 'Created', description: 'Promotion created!' });
        setSuccess('Promotion created successfully!');
      }
  
      resetForm();
      onHide();
    } catch (err) {
      console.error('Error saving promotion:', err);
      setError(err.response?.data?.message || 'Failed to save promotion');
    } finally {
      setIsLoading(false);
    }
  };  

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setImage(null);
    setPreviewUrl('');
    setShowPreview(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <Modal isOpen={show} onClose={onHide} size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Promotion</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <form onSubmit={handleSubmit}>
            <Flex direction={['column', 'row']} gap={6}>
              {/* Left: Form */}
              <VStack spacing={4} align="stretch" flex="1">
                {error && (
                  <Alert status="error">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert status="success">
                    <AlertIcon />
                    {success}
                  </Alert>
                )}

                <FormControl isRequired>
                  <FormLabel>Promotion Title</FormLabel>
                  <Input
                    placeholder="Enter promotion title"
                    value={title}
                    maxLength={100}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    placeholder="Enter promotion details"
                    value={description}
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Promotion Image</FormLabel>
                  <Input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  {previewUrl && (
                    <Box mt={2}>
                      <Image
                        src={previewUrl}
                        width="360px"
                        height="200px"
                        objectFit="cover"
                        borderRadius="md"
                      />
                    </Box>
                  )}
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <Switch
                    id="preview-switch"
                    isChecked={showPreview}
                    onChange={() => setShowPreview(!showPreview)}
                    mr={2}
                  />
                  <FormLabel htmlFor="preview-switch" mb="0">
                    Show Mobile Preview
                  </FormLabel>
                </FormControl>

                <Flex justify="flex-end" mt={4}>
                  <Button onClick={() => { resetForm(); onHide(); }} mr={3}>
                    Cancel
                  </Button>
                  <Button colorScheme="blue" type="submit" isLoading={isLoading}>
                    {promotion ? 'Update Promotion' : 'Create Promotion'}
                  </Button>
                </Flex>
              </VStack>

              {/* Right: Mobile Preview */}
              {showPreview && (
                <Box
                  width="360px"
                  height="500px"
                  border="1px solid #ccc"
                  borderRadius="xl"
                  overflow="hidden"
                  boxShadow="lg"
                  bg="gray.50"
                >
                  {/* Mobile App Header */}
                  <Box bg="blue.500" color="white" px={4} py={2} textAlign="center" fontWeight="bold">
                    iFun Baby App
                  </Box>

                  {/* Promotion Card */}
                  <VStack align="stretch" spacing={0} bg="white" m={3} borderRadius="md" overflow="hidden" boxShadow="sm">
                    {previewUrl ? (
                      <Image
                        src={previewUrl}
                        alt="Preview"
                        width="360px"
                        height="200px"
                        objectFit="cover"
                      />
                    ) : (
                      <Box
                        width="360px"
                        height="200px"
                        bg="gray.100"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text color="gray.500">No Image</Text>
                      </Box>
                    )}
                    <Box p={4}>
                      <Text fontWeight="bold" fontSize="lg" mb={2}>
                        {title || 'Promotion Title'}
                      </Text>
                      <Text fontSize="sm" color="gray.700" mb={4}>
                        {description || 'Promotion description will appear here.'}
                      </Text>
                      <Button colorScheme="blue" size="sm" width="100%">
                        Shop Now
                      </Button>
                    </Box>
                  </VStack>
                </Box>
              )}
            </Flex>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PromotionBuilderModal;
